import { Component, effect, inject, signal, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { JobCardFindDTO } from 'src/app/modules/job-operation/interfaces/job-card.entity';
import { JobCardService } from 'src/app/modules/job-operation/services/job-card.service';
import { ModalService } from 'src/app/modules/shared/services/modal.service';
import { GlobalDepartmentService } from 'src/app/core/services/global-department.service';
import { LoadingService, NotificationService } from 'src/app/core';
import { MachineService } from 'src/app/modules/shared/services/machine.service';
import { DepartmentService } from 'src/app/modules/shared/services/department.service';
import { JobOperationService } from 'src/app/modules/shared/services/job-operation.service';
import { catchError, of, switchMap, tap, forkJoin } from 'rxjs';

@Component({
  selector: 'app-planning-view',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './planning-view.component.html',
  styleUrl: './planning-view.component.scss'
})
export class PlanningViewComponent implements OnChanges {
  @Input() machineId: number | null = null;
  @Input() machineName: string | null = null;

  modal = inject(ModalService);
  jobCardService = inject(JobCardService);
  globalDepartmentService = inject(GlobalDepartmentService);
  loading = inject(LoadingService);
  notification = inject(NotificationService);
  machineService = inject(MachineService);
  departmentService = inject(DepartmentService);
  jobOperationService = inject(JobOperationService);

  pendingJobs = signal<(JobCardFindDTO & { 
    currentPass?: number; 
    uniqueJobId?: string; 
    isDuplicate?: boolean; 
    jobSequence?: number; 
    workOrderId?: string; 
    workOrderItem?: string; 
    workOrderQty?: string; 
    isFinal?: boolean;
    isLast?: boolean;
  })[]>([]);
  department_id = this.globalDepartmentService.selectedDepartmentId;
  isExpanded = signal(false);
  currentHourlyOutput = signal<number>(0);
  
  isSaving = signal(false);
  savedCount = signal(0);
  totalToSave = signal(0);
  todayDate = new Date().toISOString().split('T')[0];

  private draggedIndex: number | null = null;
  private dragOverIndex: number | null = null;
  private scrollEl: HTMLElement | null = null;
  private autoScrollSpeed = 0;
  private autoScrollRaf: number | null = null;

  private onWindowWheel = (event: WheelEvent) => {
    if (this.draggedIndex !== null && this.scrollEl) {
      this.scrollEl.scrollTop += event.deltaY;
    }
  };

  private onWindowDragOver = (event: DragEvent) => {
    if (this.draggedIndex === null || !this.scrollEl) return;
    const rect = this.scrollEl.getBoundingClientRect();
    const threshold = 100;
    const maxSpeed = 20;

    const distFromTop = event.clientY - rect.top;
    const distFromBottom = rect.bottom - event.clientY;

    if (distFromTop < threshold) {
      const factor = Math.max(0, 1 - distFromTop / threshold);
      this.autoScrollSpeed = -Math.ceil(maxSpeed * factor);
    } else if (distFromBottom < threshold) {
      const factor = Math.max(0, 1 - distFromBottom / threshold);
      this.autoScrollSpeed = Math.ceil(maxSpeed * factor);
    } else {
      this.autoScrollSpeed = 0;
    }
  };

  constructor() {
    effect(() => {
      if (this.modal.$$pendingJobs()) {
        this.fetchPendingJobs();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['machineId'] || changes['machineName']) && this.modal.$$pendingJobs()) {
      this.fetchPendingJobs();
    }
  }

  toggleExpand(): void {
    this.isExpanded.update(val => !val);
  }

  onDragStart(index: number, event?: DragEvent): void {
    this.draggedIndex = index;
    const target = event?.target as HTMLElement | undefined;
    this.scrollEl = target?.closest('.job-list-scroll') || (document.querySelector('.job-list-scroll') as HTMLElement | null);

    window.addEventListener('wheel', this.onWindowWheel, { passive: true });
    window.addEventListener('dragover', this.onWindowDragOver, { passive: true });

    this.startAutoScrollLoop();
  }

  private startAutoScrollLoop(): void {
    if (this.autoScrollRaf !== null) return;
    const loop = () => {
      if (this.draggedIndex !== null && this.scrollEl && this.autoScrollSpeed !== 0) {
        this.scrollEl.scrollTop += this.autoScrollSpeed;
      }
      if (this.draggedIndex !== null) {
        this.autoScrollRaf = requestAnimationFrame(loop);
      } else {
        this.autoScrollRaf = null;
      }
    };
    this.autoScrollRaf = requestAnimationFrame(loop);
  }

  private stopDragScroll(): void {
    window.removeEventListener('wheel', this.onWindowWheel);
    window.removeEventListener('dragover', this.onWindowDragOver);
    this.autoScrollSpeed = 0;
    if (this.autoScrollRaf !== null) {
      cancelAnimationFrame(this.autoScrollRaf);
      this.autoScrollRaf = null;
    }
    this.scrollEl = null;
  }

  onDragOver(event: DragEvent, index: number): void {
    event.preventDefault(); // Necessary to allow dropping
    this.dragOverIndex = index;
  }

  onDrop(index: number): void {
    this.stopDragScroll();
    if (this.draggedIndex === null || this.dragOverIndex === null) return;
    
    const originalIndex = this.draggedIndex;
    const draggedItem = this.pendingJobs()[this.draggedIndex];
    const updatedJobs = [...this.pendingJobs()];
    updatedJobs.splice(this.draggedIndex, 1);
    updatedJobs.splice(index, 0, draggedItem);

    this.pendingJobs.set(updatedJobs);
    this.draggedIndex = null;
    this.dragOverIndex = null;

    if (index === 0 || originalIndex === 0) {
      if (this.machineId !== null) {
        this.machineService.getNextAvailableTime(this.machineId).subscribe({
          next: (timeResponse) => {
            const nextAvailableTime = timeResponse.data.nextAvailableTime;
            const currentStartTime = new Date(nextAvailableTime);
            const localStart = new Date(currentStartTime.getTime() - (currentStartTime.getTimezoneOffset() * 60000)).toISOString().slice(0, 16);
            
            const jobs = [...this.pendingJobs()];
            if (jobs.length > 0) {
              jobs[0].printingDate = localStart;
              this.pendingJobs.set(jobs);
              this.onScheduleChange(0);
            }
          },
          error: (err) => {
            console.error('Failed to fetch next available time on drop', err);
            this.onScheduleChange(0);
          }
        });
      }
    } else {
      const minAffected = Math.min(originalIndex, index);
      const startIndex = Math.max(0, minAffected - 1);
      this.onScheduleChange(startIndex);
    }
  }

  onDragEnd(): void {
    this.stopDragScroll();
    this.draggedIndex = null;
    this.dragOverIndex = null;
  }

  recalculateSchedule(jobs: JobCardFindDTO[], baseTimeString: string): void {
    if (!baseTimeString) return;
    
    // Parse the base string ("YYYY-MM-DDTHH:mm") as local time
    let currentTime = new Date(baseTimeString);
    const hasSelection = jobs.some(j => j.selected);

    for (let i = 0; i < jobs.length; i++) {
      const job = jobs[i];
      if (hasSelection && !job.selected) continue;
      
      const yyyy = currentTime.getFullYear();
      const mm = String(currentTime.getMonth() + 1).padStart(2, '0');
      const dd = String(currentTime.getDate()).padStart(2, '0');
      const hh = String(currentTime.getHours()).padStart(2, '0');
      const min = String(currentTime.getMinutes()).padStart(2, '0');
      
      job.printingDate = `${yyyy}-${mm}-${dd}T${hh}:${min}`;

      const durationHours = job.duration || 1;
      currentTime = new Date(currentTime.getTime() + durationHours * 60 * 60 * 1000);
    }
  }

  getDateOf(dateString?: string): string {
    if (!dateString) return 'Unscheduled';
    return dateString.substring(0, 10);
  }

  isNewDate(index: number): boolean {
    const jobs = this.pendingJobs();
    if (index === 0) return true;
    
    const current = this.getDateOf(jobs[index].printingDate);
    const previous = this.getDateOf(jobs[index - 1].printingDate);
    return current !== previous;
  }

  doesSpillOver(job: JobCardFindDTO): boolean {
    if (!job.printingDate || !job.duration) return false;
    // printingDate is stored as local time string e.g., "YYYY-MM-DDTHH:mm"
    const start = new Date(job.printingDate);
    const end = new Date(start.getTime() + job.duration * 60 * 60 * 1000);
    
    return start.getDate() !== end.getDate() || start.getMonth() !== end.getMonth() || start.getFullYear() !== end.getFullYear();
  }

  hasMultiplePasses(job: any): boolean {
    return Number(job.passCount || 1) > 1;
  }

  duplicateJob(index: number) {
    const jobs = [...this.pendingJobs()];
    const jobToDuplicate = jobs[index];
    
    const newUniqueJobId = `${jobToDuplicate.uniqueJobId}_copy_${Date.now()}`;
    
    const duplicatedJob = {
      ...jobToDuplicate,
      uniqueJobId: newUniqueJobId,
      isDuplicate: true,
      jobSequence: (jobToDuplicate.jobSequence || jobToDuplicate.currentPass || 1) + 1,
      selected: false // Don't auto-select the duplicate
    };
    
    jobs.splice(index + 1, 0, duplicatedJob);
    this.pendingJobs.set(jobs);
    this.onScheduleChange(index + 1);
  }

  removeDuplicateJob(index: number) {
    const jobs = [...this.pendingJobs()];
    jobs.splice(index, 1);
    this.pendingJobs.set(jobs);
    
    // We update the schedule from the index where the job was removed
    if (index < jobs.length) {
      this.onScheduleChange(index);
    }
  }

  toggleAllSelection(event: Event) {
    const isChecked = (event.target as HTMLInputElement).checked;
    const jobs = [...this.pendingJobs()];
    jobs.forEach(j => j.selected = isChecked);
    this.pendingJobs.set(jobs);
    this.onSelectionChange();
  }

  isAllSelected(): boolean {
    const jobs = this.pendingJobs();
    return jobs.length > 0 && jobs.every(j => j.selected);
  }

  onSelectionChange(): void {
    const jobs = this.pendingJobs();
    const hasSelection = jobs.some(j => j.selected);
    const firstIndex = hasSelection ? jobs.findIndex(j => j.selected) : 0;
    if (firstIndex !== -1 && jobs.length > 0) {
      this.onScheduleChange(firstIndex);
    }
  }

  onScheduleChange(index: number): void {
    const jobs = [...this.pendingJobs()];
    if (index < 0 || index >= jobs.length) return;
    
    if (!jobs[index].printingDate) return;
    
    const hasSelection = jobs.some(j => j.selected);
    
    let currentTime = new Date(jobs[index].printingDate);
    for (let i = index; i < jobs.length; i++) {
      const job = jobs[i];
      if (hasSelection && !job.selected) continue;

      const yyyy = currentTime.getFullYear();
      const mm = String(currentTime.getMonth() + 1).padStart(2, '0');
      const dd = String(currentTime.getDate()).padStart(2, '0');
      const hh = String(currentTime.getHours()).padStart(2, '0');
      const min = String(currentTime.getMinutes()).padStart(2, '0');
      job.printingDate = `${yyyy}-${mm}-${dd}T${hh}:${min}`;

      const durationHours = job.duration || 1;
      currentTime = new Date(currentTime.getTime() + durationHours * 60 * 60 * 1000);
    }
    this.pendingJobs.set(jobs);
  }

  onImpressionChange(index: number): void {
    const jobs = [...this.pendingJobs()];
    if (index < 0 || index >= jobs.length) return;
    
    const job = jobs[index];
    const hourlyOutput = this.currentHourlyOutput();
    
    const isFlexo = job.productType?.toLowerCase().includes('flexo');
    const quantity = isFlexo ? (job.reelLength || 0) : (job.nofImpressions || 0);
    
    if (hourlyOutput > 0 && quantity >= 0) {
      job.duration = Number(((quantity / hourlyOutput) + 0.75).toFixed(2));
    }
    
    this.pendingJobs.set(jobs);
    this.onScheduleChange(index);
  }

  async savePendingJobs() {
    let jobs = this.pendingJobs();
    const selectedJobs = jobs.filter(j => j.selected);
    if (selectedJobs.length > 0) {
      jobs = selectedJobs;
    }

    if (!jobs || jobs.length === 0) return;

    const missingDeliveryDate = jobs.some(j => !j.deliveryDate);
    if (missingDeliveryDate) {
      this.notification.set({ type: 'error', message: 'Delivery date is mandatory for all jobs being updated.' });
      return;
    }

    this.isSaving.set(true);
    this.totalToSave.set(jobs.length);
    this.savedCount.set(0);

    for (let i = 0; i < jobs.length; i++) {
      const job = jobs[i];
      const isFlexo = job.productType?.toLowerCase().includes('flexo');
      const quantity = isFlexo ? (job.reelLength || 0) : (job.nofImpressions || 0);

      const printingWo = (job as any).workOrders?.find((w: any) => w.operation?.trim().toUpperCase() === 'PRINTING') || (job as any).workOrders?.[0];
      const workOrderId = (job as any).workOrderId || (printingWo ? printingWo.workOrderId : undefined);
      const workOrderName = (job as any).workOrderItem || (printingWo ? printingWo.item : null);

      let groupNo: string | null = null;
      try {
        const response: any = await new Promise((resolve, reject) => {
          this.jobOperationService.getByJobCardId(job.jobCardId, workOrderId).subscribe({
            next: (res) => resolve(res),
            error: (err) => reject(err)
          });
        });
        const existingOps = response?.data || [];
        const existingGroup = existingOps.find((op: any) => op.groupNo != null && op.groupNo !== '');
        if (existingGroup) {
            groupNo = existingGroup.groupNo;
        } else {
            if (workOrderId) {
                groupNo = `GRP-${String(workOrderId).trim()}-01`;
            } else {
                const cleanJobNo = (job.ppNo || String(job.jobCardId)).trim();
                groupNo = `GRP-${cleanJobNo}-01`;
            }
        }
      } catch (err) {
        console.error('Error fetching job card stage details:', err);
        if (workOrderId) {
            groupNo = `GRP-${String(workOrderId).trim()}-01`;
        } else {
            const cleanJobNo = (job.ppNo || String(job.jobCardId)).trim();
            groupNo = `GRP-${cleanJobNo}-01`;
        }
      }

      const isFinal = Boolean(job.isFinal || job.isLast);

      const jobOperations = [{
          stage: 'PRINTING',
          sequence: job.jobSequence || job.currentPass || 1,
          estimatedDateTime: job.printingDate ? job.printingDate.replace('T', ' ') : '',
          jobId: job.jobCardId,
          machineId: this.machineId,
          estimateTime: job.duration,
          operationQty: quantity,
          isResorting: 0,
          groupNo: groupNo,
          remarks: job.remark || null,
          jobPassNumber: job.currentPass || 1,
          workOrderId: workOrderId || null,
          workOrderName: workOrderName,
          isReprint: 0,
          isRework: 0,
          reprintReason: "",
          ncrOperators: [],
          isFinal: isFinal
      }];

      const payload = {
          jobOperations,
          deliverDate: job.deliveryDate || '',
          isFinal: isFinal
      };

      try {
        await new Promise((resolve, reject) => {
          this.jobOperationService.create(payload).subscribe({
            next: (res) => resolve(res),
            error: (err) => reject(err)
          });
        });
        this.savedCount.update(c => c + 1);
      } catch (err) {
        console.error('Failed to save job', job.jobCardId, err);
      }
    }

    setTimeout(() => {
        this.isSaving.set(false);
        this.notification.set({ type: 'success', message: 'All plans updated successfully!' });
        this.modal.$$pendingJobs.set(false);
        this.jobCardService.triggerRefresh();
    }, 1000);
  }

  fetchPendingJobs(): void {
    this.loading.set(true);
    const params = {
        pp_number: '',
        status: 'pending',
        delivery_date: '',
        machine_id: -1,
        department_id: this.department_id() ?? -1,
        page_number: 1,
        items_per_page: 100,
        request_date: '',
        cs_released_at: '',
        print_machine: this.machineName ?? ''
    };

    this.jobCardService.find(params).pipe(
      tap(response => {
        const fetchedJobs = response.data.data || [];
        
        let expandedJobs: any[] = [];
        fetchedJobs.forEach((job: any) => {
          const printingWorkOrders = (job.workOrders || []).filter(
            (wo: any) => wo.operation?.trim().toLowerCase() === 'printing'
          );

          if (printingWorkOrders.length === 0) return;

          const passCount = Number(job.passCount) || 1;
          printingWorkOrders.forEach((wo: any) => {
            const parsedQty = parseFloat(wo.qty);
            const woImpressions = !isNaN(parsedQty) ? parsedQty : (job.nofImpressions || 0);

            for (let p = 1; p <= passCount; p++) {
              expandedJobs.push({
                ...job,
                nofImpressions: woImpressions,
                workOrderId: wo.workOrderId,
                workOrderItem: wo.item,
                workOrderQty: wo.qty,
                currentPass: p,
                uniqueJobId: `${job.jobCardId}_${wo.workOrderId || wo.id}_${p}`,
                isFinal: false,
                isLast: false
              });
            }
          });
        });

        const now = Date.now();
        const sortedJobs = expandedJobs.sort((a: any, b: any) => {
          const dateA = a.requestDate ? new Date(a.requestDate).getTime() : 0;
          const dateB = b.requestDate ? new Date(b.requestDate).getTime() : 0;
          
          if (!a.requestDate && !b.requestDate) return 0;
          if (!a.requestDate) return 1;
          if (!b.requestDate) return -1;
          
          return Math.abs(dateA - now) - Math.abs(dateB - now);
        });

        sortedJobs.forEach((job: any) => {
          if (!job.deliveryDate && job.requestDate) {
            const reqDate = new Date(job.requestDate);
            if (!isNaN(reqDate.getTime())) {
              reqDate.setDate(reqDate.getDate() - 1);
              job.deliveryDate = reqDate.toISOString().split('T')[0];
            }
          }
        });

        if (this.machineId !== null && sortedJobs.length > 0) {
          forkJoin({
            machineData: this.machineService.getNextAvailableTime(this.machineId),
            departmentsData: this.departmentService.getAllDepartments()
          }).pipe(
            tap(({ machineData, departmentsData }) => {
              this.loading.set(false);

              const allMachines = departmentsData?.data?.departments?.reduce((acc: any[], dept: any) => acc.concat(dept.machines || []), []) || [];
              const currentMachine = allMachines.find((m: any) => m.machineId === this.machineId);
              const hourlyOutput = currentMachine?.hourlyOutput || 0;
              this.currentHourlyOutput.set(hourlyOutput);

              const updatedJobs = sortedJobs.map(job => {
                const isFlexo = job.productType?.toLowerCase().includes('flexo');
                const quantity = isFlexo ? (job.reelLength || 0) : (job.nofImpressions || 0);
                if (hourlyOutput > 0 && quantity >= 0) {
                  job.duration = Number(((quantity / hourlyOutput) + 0.75).toFixed(2));
                }
                return job;
              });

              const nextAvailableTime = machineData?.data?.nextAvailableTime;
              if (nextAvailableTime) {
                // Convert UTC nextAvailableTime from API into local datetime-local string
                const utcDate = new Date(nextAvailableTime);
                const offset = utcDate.getTimezoneOffset();
                const localDate = new Date(utcDate.getTime() - offset * 60000);
                const localISOTime = localDate.toISOString().slice(0, 16);
                
                this.recalculateSchedule(updatedJobs, localISOTime);
              }

              this.pendingJobs.set(updatedJobs);
            }),
            catchError(err => {
              this.loading.set(false);
              this.pendingJobs.set(sortedJobs);
              console.error('Failed to fetch additional data:', err);
              return of(null);
            })
          ).subscribe();
        } else {
          this.loading.set(false);
          this.pendingJobs.set(sortedJobs);
        }
      }),
      catchError(error => {
        this.loading.set(false);
        this.notification.set({ type: 'error', message: `Failed to fetch pending jobs` });
        console.error(error);
        return of(null);
      })
    ).subscribe();
  }
}
